import { ExamPaperStatus, QbQuestionType, QbTagCategory } from '@exam-room/graphql';
import {
  TagCategory,
  QuestionGroupType,
  QConfigStructureCategory,
} from '@exam-room/graphql/preview';

import {
  type Question,
  type PreviewQuestionMetadata,
  type QuestionTag,
  type QSetQuestion,
  type PreviewExam,
  type ExamPaper,
  type ExamPaperItem,
  type QConfigStructure,
} from './types';

interface PreviewQuestion extends Question {
  metadata: PreviewQuestionMetadata | null;
  tags: QuestionTag[];
}

const TAG_CATEGORY_MAPPER: Partial<Record<QConfigStructureCategory, QbTagCategory>> = {
  [QConfigStructureCategory.Course]: QbTagCategory.Course,
  [QConfigStructureCategory.Part]: QbTagCategory.Part,
  [QConfigStructureCategory.Subject]: QbTagCategory.Subject,
  [QConfigStructureCategory.Group]: QbTagCategory.Group,
  [QConfigStructureCategory.Chapter]: QbTagCategory.Chapter,
};

export function generateTags(qConfigStructures: QConfigStructure[]): QuestionTag[] {
  return qConfigStructures
    .map((qConfigStructure) => {
      const tagCategory =
        TAG_CATEGORY_MAPPER[qConfigStructure.category as QConfigStructureCategory];

      if (!qConfigStructure.category || !tagCategory) return null;

      return {
        id: qConfigStructure.id,
        name: qConfigStructure.name ?? '',
        type: qConfigStructure.type,
        category: tagCategory,
        refId: qConfigStructure.id,
        refTagId: null,
        refEntity: 'Q_CONFIG_STRUCTURE',
        refSource: null,
        description: null,
      };
    })
    .filter(Boolean) as QuestionTag[];
}

export function generateQuestions(
  qSetQuestions: QSetQuestion[],
  tags: QuestionTag[],
): PreviewQuestion[] {
  const sortedQSetQuestions = [...qSetQuestions].sort((a, b) => a.order - b.order);

  return sortedQSetQuestions.map<PreviewQuestion>((qSetQuestion) => {
    const { question } = qSetQuestion;

    if (!question) throw new Error(`Question not found for QSet Question ${qSetQuestion.id}`);

    const questionTags = tags.filter((tag) =>
      qSetQuestion.metadata?.qConfigStructures.find(
        (qConfigStructure) => qConfigStructure.id === tag.refId,
      ),
    );

    const difficulty =
      question.tags.find((tag) => tag.category === TagCategory.Difficulty)?.name ?? '';

    const calculation =
      question.tags.find((tag) => tag.category === TagCategory.Calculation)?.name ?? '';

    return {
      id: question.id,
      question: question.details,
      type: QbQuestionType.MultipleChoice,
      metadata: {
        choices: question.metadata.choices,
        paragraphs: question.questionGroup?.metadata?.paragraph ?? null,
        correctAnswer: { value: question.correctAnswer.value },
        solution: question.solution?.value ?? '',
        refCode: question.refCode,
        reference: question.metadata?.reference ?? '',
        learningObjectiveStatement: question.learningObjectiveStatement?.tag.name ?? '',
        score: qSetQuestion.score,
        questionId: qSetQuestion.questionId,
        questionGroupId: qSetQuestion.questionGroupId,
        size: question.questionGroup?.size ?? undefined,
        questionGroupType: question.questionGroup?.type ?? QuestionGroupType.Single,
        calculation,
        difficulty,
        questionTags,
        order: qSetQuestion.order,
      },
      tags: questionTags,
    };
  });
}

export function generateExamPaper(
  moduleExam: PreviewExam,
  questions: PreviewQuestion[],
  randomizedQuestionIds: string[],
): ExamPaper {
  const examPaper = {
    id: 'preview-exam-paper',
    examId: moduleExam.id,
    status: ExamPaperStatus.InProgress,
    totalQuestions: questions.length,
    examPaperItems: [],
  };

  const examPaperItems = generateExamPaperItems(examPaper, questions, randomizedQuestionIds);

  return {
    ...examPaper,
    examPaperItems,
  };
}

export function generateExamPaperItems(
  examPaper: ExamPaper,
  questions: PreviewQuestion[],
  randomizedQuestionIds: string[],
): ExamPaperItem[] {
  const sourceQuestions = mapRandomQuestions(questions, randomizedQuestionIds);

  return sourceQuestions.map((question, index) => ({
    id: `preview-exam-paper-item-${index + 1}`,
    examPaperId: examPaper.id,
    order: index + 1,
    question: question,
    questionId: question.id,
    score: question.metadata?.score ?? 0,
    refId: null,
    refType: null,
  }));
}

function mapRandomQuestions(
  questions: PreviewQuestion[],
  randomizedQuestionIds: string[],
): PreviewQuestion[] {
  const questionMap = new Map(questions.map((q) => [q.id, q]));

  const randomizedQuestions = randomizedQuestionIds
    .map((id) => questionMap.get(id))
    .filter((q): q is PreviewQuestion => q !== undefined);

  return randomizedQuestions;
}
